/**
 * Design Token 系统入口文件
 * 统一导出所有 Design Token 和主题配置
 */

import type { DesignTokens, ThemeMode, AntdTokenMapping } from './types';
import { colorTokens, darkColorTokens } from './colors';
import { typographyTokens } from './typography';
import { spacingTokens } from './spacing';
import { borderTokens } from './borders';
import { shadowTokens } from './shadows';
import { animationTokens } from './animations';
import { breakpointTokens } from './breakpoints';

// 导出所有 Token 类型
export type * from './types';

// 导出所有 Token 模块
export { colorTokens, darkColorTokens } from './colors';
export { typographyTokens } from './typography';
export { spacingTokens } from './spacing';
export { borderTokens } from './borders';
export { shadowTokens } from './shadows';
export { animationTokens } from './animations';
export { breakpointTokens } from './breakpoints';

// 亮色主题 Design Token
export const lightTheme: DesignTokens = {
  colors: colorTokens,
  typography: typographyTokens,
  spacing: spacingTokens,
  borders: borderTokens,
  shadows: shadowTokens,
  animations: animationTokens,
  breakpoints: breakpointTokens,
};

// 暗色主题 Design Token
export const darkTheme: DesignTokens = {
  colors: darkColorTokens,
  typography: typographyTokens,
  spacing: spacingTokens,
  borders: borderTokens,
  shadows: shadowTokens,
  animations: animationTokens,
  breakpoints: breakpointTokens,
};

// 主题映射
export const themes = {
  light: lightTheme,
  dark: darkTheme,
} as const;

// 获取主题函数
export const getTheme = (mode: ThemeMode): DesignTokens => {
  return themes[mode];
};

// Ant Design Token 映射配置 - 基于创美药业设计体系
export const antdTokenMapping: AntdTokenMapping = {
  // 品牌色映射 - 现代蓝系
  colorPrimary: colorTokens.alias.primary,           // #2563EB - 主品牌色
  colorPrimaryHover: colorTokens.alias.primaryHover, // #1D4ED8 - 悬停状态
  colorPrimaryActive: colorTokens.alias.primaryActive, // #1E40AF - 激活状态
  colorPrimaryBg: colorTokens.alias.primaryBg,       // #EFF6FF - 主色背景
  colorPrimaryBorder: colorTokens.alias.primaryBorder, // #BFDBFE - 主色边框
  
  // 语义色映射 - 功能状态色
  colorSuccess: colorTokens.alias.success,           // #2E8B57 - 成功色 (创新绿)
  colorSuccessHover: colorTokens.alias.successHover, // #059669 - 成功色悬停
  colorSuccessActive: colorTokens.alias.successActive, // #047857 - 成功色激活
  colorSuccessBg: colorTokens.alias.successBg,       // #ECFDF5 - 成功色背景
  colorSuccessBorder: colorTokens.alias.successBorder, // #D1FAE5 - 成功色边框
  
  colorWarning: colorTokens.alias.warning,           // #FF6B35 - 警告色 (活力橙)
  colorWarningHover: colorTokens.alias.warningHover, // #EA580C - 警告色悬停
  colorWarningActive: colorTokens.alias.warningActive, // #C2410C - 警告色激活
  colorWarningBg: colorTokens.alias.warningBg,       // #FFF7ED - 警告色背景
  colorWarningBorder: colorTokens.alias.warningBorder, // #FFEDD5 - 警告色边框
  
  colorError: colorTokens.alias.error,               // #EF4444 - 错误色 (医用红)
  colorErrorHover: colorTokens.alias.errorHover,     // #DC2626 - 错误色悬停
  colorErrorActive: colorTokens.alias.errorActive,   // #B91C1C - 错误色激活
  colorErrorBg: colorTokens.alias.errorBg,           // #FEF2F2 - 错误色背景
  colorErrorBorder: colorTokens.alias.errorBorder,   // #FEE2E2 - 错误色边框
  
  colorInfo: colorTokens.alias.info,                 // #2563EB - 信息色 (使用主色)
  colorInfoHover: colorTokens.alias.infoHover,       // #1D4ED8 - 信息色悬停
  colorInfoActive: colorTokens.alias.infoActive,     // #1E40AF - 信息色激活
  colorInfoBg: colorTokens.alias.infoBg,             // #EFF6FF - 信息色背景
  colorInfoBorder: colorTokens.alias.infoBorder,     // #DBEAFE - 信息色边框
  
  // 文本色映射 - 层次化文本颜色
  colorText: colorTokens.alias.textBase,             // #0F172A - 主要文本
  colorTextSecondary: colorTokens.alias.textSecondary, // #334155 - 次要文本
  colorTextTertiary: colorTokens.alias.textTertiary, // #475569 - 辅助文本
  colorTextQuaternary: colorTokens.alias.textQuaternary, // #64748B - 提示文本
  colorTextDisabled: colorTokens.alias.textDisabled, // #94A3B8 - 禁用文本
  colorTextPlaceholder: colorTokens.alias.textPlaceholder, // #94A3B8 - 占位符文本
  colorTextHeading: colorTokens.alias.textBase,      // #0F172A - 标题文本
  colorTextLabel: colorTokens.alias.textSecondary,   // #334155 - 标签文本
  colorTextDescription: colorTokens.alias.textTertiary, // #475569 - 描述文本
  
  // 背景色映射 - 层次化背景系统
  colorBgBase: colorTokens.alias.bgBase,             // #FFFFFF - 主背景
  colorBgContainer: colorTokens.alias.bgContainer,   // #FFFFFF - 容器背景
  colorBgElevated: colorTokens.alias.bgElevated,     // #FFFFFF - 悬浮背景
  colorBgLayout: colorTokens.alias.bgLayout,         // #F8FAFC - 布局背景
  colorBgSpotlight: colorTokens.alias.bgSection,     // #F1F5F9 - 聚光背景
  colorBgMask: colorTokens.alias.bgMask,             // rgba(0,0,0,0.48) - 遮罩背景
  
  // 边框色映射 - 层次化边框系统
  colorBorder: colorTokens.alias.border,             // #E2E8F0 - 默认边框
  colorBorderSecondary: colorTokens.alias.borderSecondary, // #F1F5F9 - 次要边框
  colorSplit: colorTokens.alias.borderDivider,       // #E2E8F0 - 分割线
  
  // 字体映射 - 基于创美药业设计体系
  fontFamily: typographyTokens.fontFamily.primary,        // 主字体
  fontFamilyCode: typographyTokens.fontFamily.mono,       // 代码字体
  fontSize: typographyTokens.fontSize.base,               // 基础字号 14px
  fontSizeSM: typographyTokens.fontSize.sm,               // 小字号 12px
  fontSizeLG: typographyTokens.fontSize.lg,               // 大字号 16px
  fontSizeXL: typographyTokens.fontSize.xl,               // 超大字号 18px
  fontSizeHeading1: typographyTokens.fontSize['5xl'],     // 标题1 48px
  fontSizeHeading2: typographyTokens.fontSize['4xl'],     // 标题2 36px
  fontSizeHeading3: typographyTokens.fontSize['3xl'],     // 标题3 30px
  fontSizeHeading4: typographyTokens.fontSize['2xl'],     // 标题4 24px
  fontSizeHeading5: typographyTokens.fontSize.xl,         // 标题5 18px
  fontWeightStrong: typographyTokens.fontWeight.semibold, // 加粗字重 600
  
  // 行高映射 - 优化阅读体验
  lineHeight: typographyTokens.lineHeight.normal,         // 标准行高 1.5
  lineHeightLG: typographyTokens.lineHeight.relaxed,      // 宽松行高 1.625
  lineHeightSM: typographyTokens.lineHeight.tight,        // 紧凑行高 1.25
  lineHeightHeading1: typographyTokens.lineHeight.tight,  // 标题1行高
  lineHeightHeading2: typographyTokens.lineHeight.tight,  // 标题2行高
  lineHeightHeading3: typographyTokens.lineHeight.tight,  // 标题3行高
  lineHeightHeading4: typographyTokens.lineHeight.normal, // 标题4行高
  lineHeightHeading5: typographyTokens.lineHeight.normal, // 标题5行高
  
  // 控制高度映射 - 组件高度系统
  controlHeight: 32,                                      // 标准控件高度 32px
  controlHeightSM: 24,                                    // 小控件高度 24px
  controlHeightLG: 40,                                    // 大控件高度 40px
  
  // 其他常用 Token
  wireframe: false,                                       // 线框模式
  zIndexBase: 0,                                          // 基础层级
  zIndexPopupBase: 1000,                                  // 弹窗基础层级
  opacityLoading: 0.65,                                   // 加载透明度
  
  // 内边距映射
  padding: spacingTokens.component.paddingMd,             // 基础内边距 16px
  paddingXS: spacingTokens.component.paddingXs,           // 超小内边距 8px
  paddingSM: spacingTokens.component.paddingSm,           // 小内边距 12px
  paddingLG: spacingTokens.component.paddingLg,           // 大内边距 24px
  paddingXL: spacingTokens.component.paddingXl,           // 超大内边距 32px
  
  // 外边距映射
  margin: spacingTokens.component.marginMd,               // 基础外边距 16px
  marginXS: spacingTokens.component.marginXs,             // 超小外边距 8px
  marginSM: spacingTokens.component.marginSm,             // 小外边距 12px
  marginLG: spacingTokens.component.marginLg,             // 大外边距 24px
  marginXL: spacingTokens.component.marginXl,             // 超大外边距 32px
  
  // 圆角映射 - 现代化圆角系统
  borderRadius: borderTokens.radius.base,                 // 基础圆角 6px
  borderRadiusXS: borderTokens.radius.xs,                 // 超小圆角 2px
  borderRadiusSM: borderTokens.radius.sm,                 // 小圆角 4px
  borderRadiusLG: borderTokens.radius.lg,                 // 大圆角 12px
  borderRadiusOuter: borderTokens.radius.xl,              // 外层圆角 16px
  
  // 边框宽度映射 - 精细化边框控制
  lineWidth: borderTokens.width.base,                     // 基础边框 1px
  lineWidthBold: borderTokens.width.thick,                // 粗边框 2px
  
  // 阴影映射 - 层次化阴影系统
  boxShadow: shadowTokens.base,                           // 基础阴影
  boxShadowSecondary: shadowTokens.sm,                    // 次要阴影
  boxShadowTertiary: shadowTokens.lg,                     // 第三级阴影
  
  // 动画映射 - 流畅的交互体验
  motionDurationFast: `${animationTokens.duration.fast}ms`,    // 快速动画 150ms
  motionDurationMid: `${animationTokens.duration.base}ms`,     // 中等动画 200ms
  motionDurationSlow: `${animationTokens.duration.slow}ms`,    // 慢速动画 300ms
  motionEaseOutCirc: animationTokens.easing.easeOut,           // 缓出动画
  motionEaseInOutCirc: animationTokens.easing.easeInOut,       // 缓入缓出动画
  motionEaseOut: animationTokens.easing.easeOut,              // 缓出动画
  motionEaseInOut: animationTokens.easing.easeInOut,          // 缓入缓出动画
  motionEaseOutBack: animationTokens.easing.smooth,           // 回弹缓出
  motionEaseInBack: animationTokens.easing.easeIn,            // 回弹缓入
  motionEaseInQuint: animationTokens.easing.easeIn,           // 五次方缓入
  motionEaseOutQuint: animationTokens.easing.easeOut,         // 五次方缓出
  
  // 屏幕断点 - 确保 XXL > XL 以避免 Ant Design 验证错误
  screenXS: breakpointTokens.xs,                              // 超小屏 480px
  screenSM: breakpointTokens.sm,                              // 小屏 640px
  screenMD: breakpointTokens.md,                              // 中屏 768px
  screenLG: breakpointTokens.lg,                              // 大屏 1024px
  screenXL: breakpointTokens.xl,                              // 超大屏 1280px
  screenXXL: breakpointTokens['3xl'],                         // 超超大屏 1920px (使用 3xl 避免验证错误)
};

// 暗色主题 Token 映射配置 - 基于创美药业设计体系
export const darkAntdTokenMapping: AntdTokenMapping = {
  // 品牌色映射 - 现代蓝系 (暗色主题)
  colorPrimary: darkColorTokens.alias.primary,           // #3B82F6 - 主品牌色
  colorPrimaryHover: darkColorTokens.alias.primaryHover, // #60A5FA - 悬停状态
  colorPrimaryActive: darkColorTokens.alias.primaryActive, // #2563EB - 激活状态
  colorPrimaryBg: darkColorTokens.alias.primaryBg,       // #1E3A8A - 主色背景
  colorPrimaryBorder: darkColorTokens.alias.primaryBorder, // #3B82F6 - 主色边框
  
  // 语义色映射 - 功能状态色 (暗色主题)
  colorSuccess: darkColorTokens.alias.success,           // #10B981 - 成功色
  colorSuccessHover: darkColorTokens.alias.successHover, // #34D399 - 成功色悬停
  colorSuccessActive: darkColorTokens.alias.successActive, // #059669 - 成功色激活
  colorSuccessBg: darkColorTokens.alias.successBg,       // #064E3B - 成功色背景
  colorSuccessBorder: darkColorTokens.alias.successBorder, // #10B981 - 成功色边框
  
  colorWarning: darkColorTokens.alias.warning,           // #F59E0B - 警告色
  colorWarningHover: darkColorTokens.alias.warningHover, // #FBBF24 - 警告色悬停
  colorWarningActive: darkColorTokens.alias.warningActive, // #D97706 - 警告色激活
  colorWarningBg: darkColorTokens.alias.warningBg,       // #92400E - 警告色背景
  colorWarningBorder: darkColorTokens.alias.warningBorder, // #F59E0B - 警告色边框
  
  colorError: darkColorTokens.alias.error,               // #EF4444 - 错误色
  colorErrorHover: darkColorTokens.alias.errorHover,     // #F87171 - 错误色悬停
  colorErrorActive: darkColorTokens.alias.errorActive,   // #DC2626 - 错误色激活
  colorErrorBg: darkColorTokens.alias.errorBg,           // #991B1B - 错误色背景
  colorErrorBorder: darkColorTokens.alias.errorBorder,   // #EF4444 - 错误色边框
  
  colorInfo: darkColorTokens.alias.info,                 // #3B82F6 - 信息色
  colorInfoHover: darkColorTokens.alias.infoHover,       // #60A5FA - 信息色悬停
  colorInfoActive: darkColorTokens.alias.infoActive,     // #2563EB - 信息色激活
  colorInfoBg: darkColorTokens.alias.infoBg,             // #1E3A8A - 信息色背景
  colorInfoBorder: darkColorTokens.alias.infoBorder,     // #3B82F6 - 信息色边框
  
  // 文本色映射 - 层次化文本颜色 (暗色主题)
  colorText: darkColorTokens.alias.textBase,             // #F8FAFC - 主要文本
  colorTextSecondary: darkColorTokens.alias.textSecondary, // #CBD5E1 - 次要文本
  colorTextTertiary: darkColorTokens.alias.textTertiary, // #94A3B8 - 辅助文本
  colorTextQuaternary: darkColorTokens.alias.textQuaternary, // #64748B - 提示文本
  colorTextDisabled: darkColorTokens.alias.textDisabled, // #475569 - 禁用文本
  colorTextPlaceholder: darkColorTokens.alias.textPlaceholder, // #64748B - 占位符文本
  colorTextHeading: darkColorTokens.alias.textBase,      // #F8FAFC - 标题文本
  colorTextLabel: darkColorTokens.alias.textSecondary,   // #CBD5E1 - 标签文本
  colorTextDescription: darkColorTokens.alias.textTertiary, // #94A3B8 - 描述文本
  
  // 背景色映射 - 层次化背景系统 (暗色主题)
  colorBgBase: darkColorTokens.alias.bgBase,             // #0F172A - 主背景
  colorBgContainer: darkColorTokens.alias.bgContainer,   // #1E293B - 容器背景
  colorBgElevated: darkColorTokens.alias.bgElevated,     // #334155 - 悬浮背景
  colorBgLayout: darkColorTokens.alias.bgLayout,         // #020617 - 布局背景
  colorBgSpotlight: darkColorTokens.alias.bgSection,     // #1E293B - 聚光背景
  colorBgMask: darkColorTokens.alias.bgMask,             // rgba(0,0,0,0.65) - 遮罩背景
  
  // 边框色映射 - 层次化边框系统 (暗色主题)
  colorBorder: darkColorTokens.alias.border,             // #334155 - 默认边框
  colorBorderSecondary: darkColorTokens.alias.borderSecondary, // #1E293B - 次要边框
  colorSplit: darkColorTokens.alias.borderDivider,       // #334155 - 分割线
  
  // 其他 Token 保持一致 (字体、间距、圆角、阴影、动画、断点等)
  fontFamily: typographyTokens.fontFamily.primary,
  fontFamilyCode: typographyTokens.fontFamily.mono,
  fontSize: typographyTokens.fontSize.base,
  fontSizeSM: typographyTokens.fontSize.sm,
  fontSizeLG: typographyTokens.fontSize.lg,
  fontSizeXL: typographyTokens.fontSize.xl,
  fontSizeHeading1: typographyTokens.fontSize['5xl'],
  fontSizeHeading2: typographyTokens.fontSize['4xl'],
  fontSizeHeading3: typographyTokens.fontSize['3xl'],
  fontSizeHeading4: typographyTokens.fontSize['2xl'],
  fontSizeHeading5: typographyTokens.fontSize.xl,
  fontWeightStrong: typographyTokens.fontWeight.semibold,
  
  lineHeight: typographyTokens.lineHeight.normal,
  lineHeightLG: typographyTokens.lineHeight.relaxed,
  lineHeightSM: typographyTokens.lineHeight.tight,
  lineHeightHeading1: typographyTokens.lineHeight.tight,
  lineHeightHeading2: typographyTokens.lineHeight.tight,
  lineHeightHeading3: typographyTokens.lineHeight.tight,
  lineHeightHeading4: typographyTokens.lineHeight.normal,
  lineHeightHeading5: typographyTokens.lineHeight.normal,
  
  controlHeight: 32,
  controlHeightSM: 24,
  controlHeightLG: 40,
  
  wireframe: false,
  zIndexBase: 0,
  zIndexPopupBase: 1000,
  opacityLoading: 0.65,
  
  padding: spacingTokens.component.paddingMd,
  paddingXS: spacingTokens.component.paddingXs,
  paddingSM: spacingTokens.component.paddingSm,
  paddingLG: spacingTokens.component.paddingLg,
  paddingXL: spacingTokens.component.paddingXl,
  
  margin: spacingTokens.component.marginMd,
  marginXS: spacingTokens.component.marginXs,
  marginSM: spacingTokens.component.marginSm,
  marginLG: spacingTokens.component.marginLg,
  marginXL: spacingTokens.component.marginXl,
  
  borderRadius: borderTokens.radius.base,
  borderRadiusXS: borderTokens.radius.xs,
  borderRadiusSM: borderTokens.radius.sm,
  borderRadiusLG: borderTokens.radius.lg,
  borderRadiusOuter: borderTokens.radius.xl,
  
  lineWidth: borderTokens.width.base,
  lineWidthBold: borderTokens.width.thick,
  
  boxShadow: shadowTokens.base,
  boxShadowSecondary: shadowTokens.sm,
  boxShadowTertiary: shadowTokens.lg,
  
  motionDurationFast: `${animationTokens.duration.fast}ms`,
  motionDurationMid: `${animationTokens.duration.base}ms`,
  motionDurationSlow: `${animationTokens.duration.slow}ms`,
  motionEaseOutCirc: animationTokens.easing.easeOut,
  motionEaseInOutCirc: animationTokens.easing.easeInOut,
  motionEaseOut: animationTokens.easing.easeOut,
  motionEaseInOut: animationTokens.easing.easeInOut,
  motionEaseOutBack: animationTokens.easing.smooth,
  motionEaseInBack: animationTokens.easing.easeIn,
  motionEaseInQuint: animationTokens.easing.easeIn,
  motionEaseOutQuint: animationTokens.easing.easeOut,
  
  screenXS: breakpointTokens.xs,
  screenSM: breakpointTokens.sm,
  screenMD: breakpointTokens.md,
  screenLG: breakpointTokens.lg,
  screenXL: breakpointTokens.xl,
  screenXXL: breakpointTokens['3xl'],
};

// 获取 Ant Design Token 映射函数
export const getAntdTokenMapping = (mode: ThemeMode): AntdTokenMapping => {
  return mode === 'dark' ? darkAntdTokenMapping : antdTokenMapping;
};

// CSS 变量生成器
export const generateCSSVariables = (theme: DesignTokens): Record<string, string> => {
  const cssVars: Record<string, string> = {};
  
  // 颜色变量
  Object.entries(theme.colors.alias).forEach(([key, value]) => {
    cssVars[`--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`] = value;
  });
  
  // 字体变量
  cssVars['--font-family-primary'] = theme.typography.fontFamily.primary;
  cssVars['--font-family-secondary'] = theme.typography.fontFamily.secondary;
  cssVars['--font-family-mono'] = theme.typography.fontFamily.mono;
  
  Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
    cssVars[`--font-size-${key}`] = `${value}px`;
  });
  
  Object.entries(theme.typography.fontWeight).forEach(([key, value]) => {
    cssVars[`--font-weight-${key}`] = value.toString();
  });
  
  Object.entries(theme.typography.lineHeight).forEach(([key, value]) => {
    cssVars[`--line-height-${key}`] = value.toString();
  });
  
  // 间距变量
  Object.entries(theme.spacing.base).forEach(([key, value]) => {
    cssVars[`--spacing-${key}`] = `${value}px`;
  });
  
  // 边框变量
  Object.entries(theme.borders.radius).forEach(([key, value]) => {
    cssVars[`--border-radius-${key}`] = `${value}px`;
  });
  
  Object.entries(theme.borders.width).forEach(([key, value]) => {
    cssVars[`--border-width-${key}`] = `${value}px`;
  });
  
  // 阴影变量
  Object.entries(theme.shadows).forEach(([key, value]) => {
    cssVars[`--shadow-${key}`] = value;
  });
  
  // 动画变量
  Object.entries(theme.animations.duration).forEach(([key, value]) => {
    cssVars[`--duration-${key}`] = `${value}ms`;
  });
  
  Object.entries(theme.animations.easing).forEach(([key, value]) => {
    cssVars[`--easing-${key}`] = value;
  });
  
  // 断点变量
  Object.entries(theme.breakpoints).forEach(([key, value]) => {
    cssVars[`--breakpoint-${key}`] = `${value}px`;
  });
  
  return cssVars;
};

// 默认导出
export default {
  lightTheme,
  darkTheme,
  themes,
  getTheme,
  antdTokenMapping,
  darkAntdTokenMapping,
  getAntdTokenMapping,
  generateCSSVariables,
};